import { GameModel } from "./gamemodel.js";
import { Constants } from "./constants.js";
import { IdGenerator } from "./idgenerator.js";
import { Log } from "./log.js";
import { RoundController } from "./roundcontroller.js";
import { RoundModel } from "./roundmodel.js";
import { Weapon } from "./weapon.js";
import { Upgrade } from "./upgrade.js";
import { Zone } from "./zone.js";
import { ScoreState } from "./scorestate.js";
import { EmblemState } from "./emblemstate.js";
import { MazeThemeManager } from "./mazethememanager.js";

export class GameController {
    model = null;
    roundController = null;
    roundEventListeners = [];
    gameEventListeners = [];
    id = null;
    localPlayerIds = [];
    log = null;
    ranked = false;
    symmetric = false;
    storm = false;
    crateTypes = [];
    premium = false;
    gameMode = null;
    betweenRoundsDuration = Constants.BETWEEN_ROUNDS_DURATION + Constants.CELEBRATION_DURATION;
    celebrationDuration = 0;
    celebrationStarted = true;
    celebrationEnded = true;
    playerAdded = false;
    lastUpdate = 0;
    countDownValue = Constants.COUNTDOWN_START_VALUE;
    countDownDuration = 0.0;

    // This constructor is used for server and offline client.
    // Called without arguments, it allocates a bare instance for withIds.
    constructor(gameMode, ranked, symmetric, storm, maxActivePlayerCount, crateTypes, premium, theme) {
        if (gameMode === undefined) {
            return;
        }

        this.id = IdGenerator.generate("gc");
        this.gameMode = gameMode;
        this.ranked = ranked;
        this.symmetric = symmetric;
        this.storm = storm;
        this.crateTypes = crateTypes;
        this.premium = premium;
        this.gameMode.setGameController(this);
        this._initialise(this.gameMode.getMode(), maxActivePlayerCount, theme);
    }

    // This constructor is only used for online client.
    static withIds(id, localPlayerIds) {
        const instance = new this();
        instance.id = id;
        instance.localPlayerIds = localPlayerIds;
        instance._initialise(null, null, null);
        return instance;
    }

    _initialise(mode, maxActivePlayerCount, theme) {
        this.log = new Log("GameController");
        this.model = new GameModel(this.id, mode, maxActivePlayerCount, theme);
        // We only want to handle events generated by the model when we are not
        // receiving instructions from the outside (ie. only if we are in local mode,
        // or in server mode)
        if (Constants.getMode() === Constants.MODE_SERVER || Constants.getMode() === Constants.MODE_CLIENT_LOCAL) {
            this.model.addEventListener(this._gameModelEventHandler, this, this.id);
        }

        // Set initial state.
        this.model.setState(GameModel._STATES.BETWEEN_ROUNDS);

        // Create round controller.
        this._createRoundController();
    }

    /**
     * Move GameController to the next frame
     */
    update() {
        const time = new Date();
        let deltaTime = (time - this.lastUpdate) / 1000.0;
        this.lastUpdate = time;

        // Modify deltaTime if too large.
        deltaTime = Math.min(deltaTime, Constants.MAX_DELTA_TIME);

        if (this.roundController) {
            this.roundController.update(deltaTime);
        }

        // Only do game logic if local client or server.
        if (Constants.getMode() === Constants.MODE_CLIENT_LOCAL || Constants.getMode() === Constants.MODE_SERVER) {
            switch (this.model.getState()) {
                case GameModel._STATES.BETWEEN_ROUNDS:
                {
                    // Wait a few seconds before creating celebration..
                    if (Constants.GAME_MODE_INFO[this.model.getMode()].HAS_CELEBRATION) {
                        if (!this.celebrationStarted) {
                            this.celebrationDuration += deltaTime;
                            if (this.celebrationDuration > Constants.BETWEEN_ROUNDS_DURATION) {
                                this.celebrationDuration = 0.0;
                                this._initialiseCelebration();
                            }
                        }
                    }

                    // Wait a few seconds before creating next round..
                    let durationToWait = Constants.BETWEEN_ROUNDS_DURATION;
                    if (Constants.GAME_MODE_INFO[this.model.getMode()].HAS_CELEBRATION) {
                        durationToWait += Constants.CELEBRATION_DURATION;
                    }
                    this.betweenRoundsDuration += deltaTime;
                    if (this.betweenRoundsDuration > durationToWait) {
                        if (!this.celebrationEnded) {
                            // This ensures that players wanting to leave, can do so.
                            this._endCelebration();
                        }

                        if (this.model.getTotalPlayerCount() >= Constants.GAME_MODE_INFO[this.model.getMode()].MIN_PLAYERS) {
                            this.betweenRoundsDuration = 0.0;
                            this.celebrationStarted = false;
                            this.celebrationEnded = false;
                            this.log.debug("Enough players available in game to start playing");
                            // This call also sets the state to COUNTING_DOWN
                            this._initialiseRound();
                        } else {
                            // Intentionally do nothing here
                        }
                    }
                    break;
                }
                case GameModel._STATES.COUNTING_DOWN:
                {
                    // Manage the countdown
                    this.countDownDuration += deltaTime;
                    if (this.countDownDuration >= Constants.COUNTDOWN_DURATION) {
                        this.countDownDuration = 0.0;
                        if (this.countDownValue > 0) {
                            this.countDown(this.countDownValue);
                            --this.countDownValue;
                        } else {
                            this.countDownValue = Constants.COUNTDOWN_START_VALUE;
                            this.startRound();
                        }
                    }
                    break;
                }
                case GameModel._STATES.IN_ROUND:
                {
                    break;
                }
                default:
                {
                    this.log.error(`Unknown state ${this.model.getState()} in GameModel._gameModelStateHandler`);
                }
            }

            if (Constants.getMode() === Constants.MODE_SERVER) {
                // If there are no players left, game has ended
                if (this.model.getTotalPlayerCount() === 0) {
                    this.model.setState(GameModel._STATES.ENDED);
                }
            } else if (Constants.getMode() === Constants.MODE_CLIENT_LOCAL) {
                // If there is only one player left, game has ended
                if (this.model.getTotalPlayerCount() <= 1) {
                    this.model.setState(GameModel._STATES.ENDED);
                }
            }

            // Update model.
            this.model.update();
        }
    }

    getId() {
        return this.id;
    }

    getRoundId() {
        if (this.roundController) {
            return this.roundController.getId();
        } else {
            return null;
        }
    }

    getMaze() {
        if (this.roundController) {
            return this.roundController.getMaze();
        } else {
            return null;
        }
    }

    getModifier(playerId, modifierType) {
        if (this.roundController) {
            return this.roundController.getModifier(playerId, modifierType);
        } else {
            return null;
        }
    }

    verifyAndCorrectTankState(tankState) {
        if (this.roundController) {
            return this.roundController.verifyAndCorrectTankState(tankState);
        }

        return false;
    }

    setTankState(tankState) {
        if (this.model.getState() !== GameModel._STATES.IN_ROUND) {
            this.log.warn(`Player with id ${tankState.getPlayerId()} attempted to set tank state with game in state ${this.model.getState()}`);
            return false;
        }
        if (!this.model.isPlayerActive(tankState.getPlayerId())) {
            this.log.warn(`Player with id ${tankState.getPlayerId()} attempted to set tank state while not active in game`);
            return false;
        }

        if (this.roundController) {
            return this.roundController.setTankState(tankState, false);
        }

        return false;
    }

    addPlayer(playerId) {
        const success = this.model.addPlayer(playerId, Constants.TEAMS.NO_TEAM);
        if (success) {
            // Only set up scores if local client or server.
            if (Constants.getMode() === Constants.MODE_CLIENT_LOCAL || Constants.getMode() === Constants.MODE_SERVER) {
                const scoreStates = this.gameMode.getScoreStates(playerId);
                for (const scoreState of scoreStates) {
                    this.setScoreState(scoreState);
                }
            }

            this.playerAdded = true;
        }

        return success;
    }

    removePlayer(playerId) {
        this.log.debug(`Removing player ${playerId}`);
        if (this.model.isPlayerActive(playerId)) {
            this.log.debug(`Player ${playerId} is marked as active, so removing from round`);
            if (this.roundController && this.roundController.getTank(playerId) !== undefined) {
                this.roundController.removeTank(playerId);
            }
        } else {
            this.log.debug(`Player ${playerId} not marked as active, so not removing from round`);
        }
        return this.model.removePlayer(playerId);
    }

    canRemovePlayers(playerIds) {
        // If game is not in a round, it's always okay to remove players.
        if (this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS || this.model.getState() === GameModel._STATES.ENDED) {
            return true;
        }

        // Otherwise, we need to ask the game mode for each active player.
        for (const playerId of playerIds) {
            if (this.model.isPlayerActive(playerId)) {
                if (!this.gameMode.canRemovePlayer(playerId)) {
                    return false;
                }
            }
        }

        return true;
    }

    setScore(scoreId, value) {
        const score = this.getScore(scoreId);
        if (score) {
            const scoreState = ScoreState.withState(
                score.getId(),
                score.getPlayerId(),
                score.getType(),
                value
            );

            this.setScoreState(scoreState);
        } else {
            this.log.error("Attempted to set non-existing score");
        }
    }

    adjustScore(scoreId, adjustment) {
        const score = this.getScore(scoreId);
        if (score) {
            const scoreState = ScoreState.withState(
                score.getId(),
                score.getPlayerId(),
                score.getType(),
                score.getValue() + adjustment
            );

            this.setScoreState(scoreState);
        } else {
            this.log.error("Attempted to adjust non-existing score");
        }
    }

    removeScore(scoreId) {
        this.log.debug(`Removing score ${scoreId}`);
        return this.model.removeScore(scoreId);
    }

    addEmblem(playerId, type) {
        const emblemState = EmblemState.withState(
            IdGenerator.generate("e"),
            playerId,
            type
        );

        this.setEmblemState(emblemState);
    }

    removeEmblem(emblemId) {
        this.log.debug(`Removing emblem ${emblemId}`);
        return this.model.removeEmblem(emblemId);
    }

    getTotalPlayerCount() {
        return this.model.getTotalPlayerCount();
    }

    getQueuedPlayerCount() {
        return this.model.getQueuedPlayerCount();
    }

    getActivePlayerCount() {
        return this.model.getActivePlayerCount();
    }

    getQueuedPlayerIds() {
        return this.model.getQueuedPlayerIds();
    }

    getActivePlayerIds() {
        return this.model.getActivePlayerIds();
    }

    getAllPlayerIds() {
        return this.model.getAllPlayerIds();
    }

    setGameState(gameState) {
        // Iterate over playerstates and call setPlayerState.
        const playerStates = gameState.getPlayerStates();
        for (const playerState of playerStates) {
            this.setPlayerState(playerState);
        }

        // Iterate over active players and check if they are still in the gamestate.
        const activePlayers = this.model.getActivePlayers();
        for (const player in activePlayers) {
            const playerId = activePlayers[player].getId();
            const playerFound = playerStates.some(playerState => playerState.getPlayerId() === playerId);
            if (!playerFound) {
                this.removePlayer(playerId);
            }
        }

        this.setMode(gameState.getMode());
        this.setMaxActivePlayerCount(gameState.getMaxActivePlayerCount());
        this.setTheme(gameState.getTheme());
        this.setRanked(gameState.getRanked());
        this.setSymmetric(gameState.getSymmetric());
        this.setStorm(gameState.getStorm());
        this.setPremium(gameState.getPremium());

        // Iterate over scoreStates and call setScoreState.
        const scoreStates = gameState.getScoreStates();
        for (const scoreState of scoreStates) {
            this.setScoreState(scoreState);
        }

        // Iterate over scores and check if they are still in the gamestate.
        const scores = this.model.getScores();
        for (const score in scores) {
            const scoreId = scores[score].getId();
            const scoreFound = scoreStates.some(scoreState => scoreState.getId() === scoreId);
            if (!scoreFound) {
                this.removeScore(scoreId);
            }
        }

        // Iterate over emblemStates and call setEmblemState.
        const emblemStates = gameState.getEmblemStates();
        for (const emblemState of emblemStates) {
            this.setEmblemState(emblemState);
        }

        // Iterate over emblems and check if they are still in the gamestate.
        const emblems = this.model.getEmblems();
        for (const emblem in emblems) {
            const emblemId = emblems[emblem].getId();
            const emblemFound = emblemStates.some(emblemState => emblemState.getId() === emblemId);
            if (!emblemFound) {
                this.removeEmblem(emblemId);
            }
        }

        this.model.emitGameState(gameState);
    }

    setPlayerState(playerState) {
        this.model.setPlayerState(playerState);
    }

    setMode(mode) {
        this.model.setMode(mode);
    }

    getMode() {
        return this.model.getMode();
    }

    setMaxActivePlayerCount(maxActivePlayerCount) {
        this.model.setMaxActivePlayerCount(maxActivePlayerCount);
    }

    getMaxActivePlayerCount() {
        return this.model.getMaxActivePlayerCount();
    }

    setTheme(theme) {
        this.model.setTheme(theme);
    }

    getTheme() {
        return this.model.getTheme();
    }

    setScoreState(scoreState) {
        this.model.setScoreState(scoreState);
    }

    setEmblemState(emblemState) {
        this.model.setEmblemState(emblemState);
    }

    getState() {
        return this.model.getState();
    }

    getGameState() {
        const gameState = this.model.getGameState();
        gameState.setId(this.id);
        gameState.setRanked(this.ranked);
        gameState.setSymmetric(this.symmetric);
        gameState.setStorm(this.storm);
        gameState.setPremium(this.premium);

        return gameState;
    }

    getRoundState() {
        if (this.roundController) {
            return this.roundController.getRoundState(this.playerAdded);
        } else {
            return null;
        }
    }

    getGameMode() {
        return this.gameMode;
    }

    setRanked(ranked) {
        this.ranked = ranked;
    }

    getRanked() {
        return this.ranked;
    }

    setSymmetric(symmetric) {
        this.symmetric = symmetric;
    }

    getSymmetric() {
        return this.symmetric;
    }

    setStorm(storm) {
        this.storm = storm;
    }

    getStorm() {
        return this.storm;
    }

    setPremium(premium) {
        this.premium = premium;
    }

    getPremium() {
        return this.premium;
    }

    setVictoryGoldAmount(victoryGoldAmount) {
        if (this.roundController) {
            this.roundController.setVictoryGoldAmount(victoryGoldAmount);
        }
    }

    setStakes(stakes) {
        if (this.roundController) {
            this.roundController.setStakes(stakes);
        }
    }

    getStake(playerId) {
        if (this.roundController) {
            return this.roundController.getStake(playerId);
        } else {
            return null;
        }
    }

    clearExpandedRoundStateBits() {
        this.playerAdded = false;
        if (this.roundController) {
            this.roundController.clearExpandedRoundStateBits();
        }
    }

    getInitialRoundStateReceived() {
        if (this.roundController) {
            return this.roundController.getInitialRoundStateReceived();
        } else {
            return false;
        }
    }

    setRoundState(roundState) {
        if (this.roundController) {
            this.roundController.setRoundState(roundState);
        }
    }

    setInputState(inputState) {
        if (this.roundController) {
            this.roundController.setInputState(inputState);
        }
    }

    _createRoundController() {
        this.log.debug(`Game id: ${this.id}. Queued player count: ${this.getQueuedPlayerCount()}`);
        this.log.debug(`Game id: ${this.id}. Active player count: ${this.getActivePlayerCount()}`);
        if (this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS) {
            // Ensure that old objects get released.
            this.roundController = null;

            // Create a new round controller
            this.roundController = new RoundController(this.localPlayerIds, this.gameMode, this.id);

            // We only want to handle events generated by the model when we are not
            // receiving instructions from the outside (ie. only if we are in local mode,
            // or in server mode)
            if (Constants.getMode() === Constants.MODE_SERVER || Constants.getMode() === Constants.MODE_CLIENT_LOCAL) {
                this.roundController.addEventListener(this._roundModelEventHandler, this, this.id);
            }
            // Attach other event listeners.
            for (const roundEventListener of this.roundEventListeners) {
                this.roundController.addEventListener(roundEventListener.callback, roundEventListener.context, this.id);
            }
        } else {
            this.log.error(`Attempt to create round in game which is currently in state ${this.model.getState()}`);
        }
    }

    setMaze(maze) {
        if (this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS || this.model.getState() === GameModel._STATES.COUNTING_DOWN || this.model.getState() === GameModel._STATES.IN_ROUND) {
            this.roundController.setMaze(maze);
        } else {
            this.log.error(`Attempt to set maze in game which is currently in state ${this.model.getState()}`);
        }
    }

    spawnGold() {
        if (Constants.getMode() === Constants.MODE_SERVER && this.model.getState() === GameModel._STATES.IN_ROUND) {
            if (this.roundController) {
                this.roundController.spawnGold();
            }
        }
    }

    spawnDiamond() {
        if (Constants.getMode() === Constants.MODE_SERVER && this.model.getState() === GameModel._STATES.IN_ROUND) {
            if (this.roundController) {
                this.roundController.spawnDiamond();
            }
        }
    }

    createRound(ranked) {
        if (Constants.getMode() === Constants.MODE_CLIENT_ONLINE && this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS) {
            // Store ranked status.
            this.ranked = ranked;

            // Create new round controller.
            this._createRoundController();

            if (this.roundController) {
                this.roundController.createRound(ranked);
            }
        }
    }

    startRound() {
        if (this.model.getState() === GameModel._STATES.COUNTING_DOWN) {
            if (this.roundController) {
                this.roundController.startRound();
            }

            // Change state to being in round.
            this.model.setState(GameModel._STATES.IN_ROUND);
        } else if (this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS && Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            if (this.roundController) {
                this.roundController.startRound();
            }

            this.log.debug("In state BETWEEN_ROUNDS when round started. Player is spectating and joined after last countdown message.");
        } else {
            this.log.error(`Attempt to start round in game which is currently in state ${this.model.getState()}`);
        }
    }

    startCelebration() {
        if (Constants.getMode() === Constants.MODE_CLIENT_ONLINE && this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS) {
            if (this.roundController) {
                this.roundController.startCelebration();
            }
        }
    }

    _initialiseCelebration() {
        this.celebrationStarted = true;

        // Call create to notify listeners of celebration's creation.
        this.roundController.startCelebration();
    }

    _endCelebration() {
        this.celebrationEnded = true;

        this.roundController.endCelebration();
    }

    _initialiseRound() {
        let nextQueuedPlayerId = this.model.getNextQueuedPlayerId();
        while (this.model.getActivePlayerCount() < this.model.getMaxActivePlayerCount() && (nextQueuedPlayerId !== null)) {
            // While there is room in the game, and more queued players,
            // move players into the active players list
            this.log.debug(`Activating player ${nextQueuedPlayerId}`);
            this.model.activatePlayer(nextQueuedPlayerId);
            nextQueuedPlayerId = this.model.getNextQueuedPlayerId();
        }

        // Update game mode with ranked status.
        this.gameMode.setRanked(this.ranked);

        // Update game mode with symmetric status.
        this.gameMode.setSymmetric(this.symmetric);

        // Update game mode with storm status.
        this.gameMode.setStorm(this.storm);

        // Update game mode with crate types.
        this.gameMode.setCrateTypes(this.crateTypes);

        // Create new round controller.
        this._createRoundController();

        // Call create to notify listeners of round's creation.
        this.roundController.createRound(this.ranked);

        // Create maze.
        const maze = this.gameMode.getMaze(this.getActivePlayerIds(), this.model.getTheme() !== Constants.MAZE_THEMES.RANDOM ? this.model.getTheme() : MazeThemeManager.getRandomActiveTheme());

        // Set maze on the round.
        this.setMaze(maze);

        // Stuff appropriate players into the round controller
        // in the form of tanks
        const tankPositions = maze.getTankPositions();
        for (const tankPosition of tankPositions) {
            this.roundController.spawnTank(tankPosition.playerId, tankPosition, false);
        }

        // Tell the game mode to reset for a new round.
        this.gameMode.initialiseRound();

        // Change state to counting down.
        this.model.setState(GameModel._STATES.COUNTING_DOWN);
    }

    countDown(value) {
        if (this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS) {
            this.model.countDown(value);

            // Change state to counting down.
            this.model.setState(GameModel._STATES.COUNTING_DOWN);
        } else if (this.model.getState() === GameModel._STATES.COUNTING_DOWN) {
            this.model.countDown(value);
        } else {
            this.log.error(`Attempt to count down in game which is currently in state ${this.model.getState()}`);
        }
    }

    endRound(victoryAward) {
        if (this.model.getState() === GameModel._STATES.IN_ROUND) {
            this.roundController.endRound(victoryAward);

            // Change state to between rounds.
            this.model.setState(GameModel._STATES.BETWEEN_ROUNDS);
        } else if (this.model.getState() === GameModel._STATES.BETWEEN_ROUNDS && Constants.getMode() === Constants.MODE_CLIENT_ONLINE) {
            this.roundController.endRound(victoryAward);

            this.log.debug("In state BETWEEN_ROUNDS when round ended. Player was spectating and joined after last countdown message.");
        } else {
            this.log.error(`Attempt to end round in game which is currently in state ${this.model.getState()}`);
        }
    }

    endGame() {
        this.model.setState(GameModel._STATES.ENDED);
    }

    getBetweenRoundsDuration() {
        return this.betweenRoundsDuration;
    }

    getQueuedPlayer(playerId) {
        return this.model.getQueuedPlayer(playerId);
    }

    getActivePlayer(playerId) {
        return this.model.getActivePlayer(playerId);
    }

    getScore(scoreId) {
        return this.model.getScore(scoreId);
    }

    getScores() {
        return this.model.getScores();
    }

    getScoreByPlayerIdAndType(playerId, type) {
        return this.model.getScoreByPlayerIdAndType(playerId, type);
    }

    getEmblem(emblemId) {
        return this.model.getEmblem(emblemId);
    }

    getEmblems() {
        return this.model.getEmblems();
    }

    getEmblemByPlayerIdAndType(playerId, type) {
        return this.model.getEmblemByPlayerIdAndType(playerId, type);
    }

    getTank(playerId) {
        if (this.roundController) {
            return this.roundController.getTank(playerId);
        }
    }

    getTanks() {
        if (this.roundController) {
            return this.roundController.getTanks();
        }
    }

    getProjectile(projectileId) {
        if (this.roundController) {
            return this.roundController.getProjectile(projectileId);
        }
    }

    getProjectiles() {
        if (this.roundController) {
            return this.roundController.getProjectiles();
        }
    }

    getTrap(trapId) {
        if (this.roundController) {
            return this.roundController.getTrap(trapId);
        }
    }

    getTraps() {
        if (this.roundController) {
            return this.roundController.getTraps();
        }
    }

    getCollectible(collectibleId) {
        if (this.roundController) {
            return this.roundController.getCollectible(collectibleId);
        }
    }

    getCollectibles() {
        if (this.roundController) {
            return this.roundController.getCollectibles();
        }
    }

    getActiveWeapon(playerId) {
        if (this.roundController) {
            return this.roundController.getActiveWeapon(playerId);
        }
    }

    getDefaultWeapon(playerId) {
        if (this.roundController) {
            return this.roundController.getDefaultWeapon(playerId);
        }
    }

    getQueuedWeapons(playerId) {
        if (this.roundController) {
            return this.roundController.getQueuedWeapons(playerId);
        }
    }

    getUpgrades() {
        if (this.roundController) {
            return this.roundController.getUpgrades();
        }
    }

    getUpgrade(upgradeId) {
        if (this.roundController) {
            return this.roundController.getUpgrade(upgradeId);
        }
    }

    getUpgradeByPlayerIdAndType(playerId, type) {
        if (this.roundController) {
            return this.roundController.getUpgradeByPlayerIdAndType(playerId, type);
        }
    }

    getCounters() {
        if (this.roundController) {
            return this.roundController.getCounters();
        }
    }

    getCounter(counterId) {
        if (this.roundController) {
            return this.roundController.getCounter(counterId);
        }
    }

    getZones() {
        if (this.roundController) {
            return this.roundController.getZones();
        }
    }

    getZone(zoneId) {
        if (this.roundController) {
            return this.roundController.getZone(zoneId);
        }
    }

    killTank(kill) {
        if (this.roundController) {
            this.roundController.killTank(kill);
        }
    }

    destroyTank(playerId) {
        if (this.roundController) {
            this.roundController.destroyTank(playerId);
        }
    }

    timeoutProjectile(projectileId) {
        if (this.roundController) {
            this.roundController.timeoutProjectile(projectileId);
        }
    }

    destroyProjectile(projectileId) {
        if (this.roundController) {
            this.roundController.destroyProjectile(projectileId);
        }
    }

    tripTrap(trip) {
        if (this.roundController) {
            this.roundController.tripTrap(trip);
        }
    }

    destroyTrap(trapId) {
        if (this.roundController) {
            this.roundController.destroyTrap(trapId);
        }
    }

    destroyCollectible(pickup) {
        if (this.roundController) {
            this.roundController.destroyCollectible(pickup);
        }
    }

    destroyWeapon(weaponDeactivation) {
        if (this.roundController) {
            this.roundController.destroyWeapon(weaponDeactivation);
        }
    }

    destroyUpgrade(upgradeUpdate) {
        if (this.roundController) {
            this.roundController.destroyUpgrade(upgradeUpdate);
        }
    }

    destroyCounter(counterId) {
        if (this.roundController) {
            this.roundController.destroyCounter(counterId);
        }
    }

    destroyZone(zoneId) {
        if (this.roundController) {
            this.roundController.destroyZone(zoneId);
        }
    }

    addGameEventListener(callback, context) {
        this.model.addEventListener(callback, context, this.id);
    }

    removeGameEventListener(callback, context) {
        this.model.removeEventListener(callback, context);
    }

    addRoundEventListener(callback, context) {
        this.roundEventListeners.push({ callback, context });

        // Hook up event listener in case round has already been created.
        if (this.roundController) {
            this.roundController.addEventListener(callback, context, this.id);
        }
    }

    removeRoundEventListener(callback, context) {
        const index = this.roundEventListeners.findIndex(listener => listener.callback === callback && listener.context === context);
        if (index !== -1) {
            this.roundEventListeners.splice(index, 1);
        }
    }

    _gameModelEventHandler(self, id, evt, data) {
        self.log.debug(`${evt} (game id ${id})`, data);

        // Handle event.
        switch (evt) {
            case GameModel._EVENTS.PLAYER_ADDED_QUEUE:
            {
                // Assign team to player.
                const player = self.model.getQueuedPlayer(data);
                if (player) {
                    player.setTeam(self.gameMode.getTeam(data));
                }
                break;
            }
            case GameModel._EVENTS.PLAYER_ADDED_ACTIVE:
            {
                // Assign team to player.
                const player = self.model.getActivePlayer(data);
                if (player) {
                    player.setTeam(self.gameMode.getTeam(data));
                }
                break;
            }
            case GameModel._EVENTS.PLAYER_REMOVED:
            case GameModel._EVENTS.PLAYERS_ADDED_ACTIVE:
            case GameModel._EVENTS.PLAYERS_ADDED_QUEUE:
            case GameModel._EVENTS.PLAYERS_REMOVED:
            case GameModel._EVENTS.GAME_STARTED:
            case GameModel._EVENTS.COUNT_DOWN:
            case GameModel._EVENTS.GAME_STATE_CHANGED:
            case GameModel._EVENTS.GAME_ENDED:
            {
                // Intentionally do nothing here
                break;
            }
            default:
            {
                self.log.error(`Unknown event ${evt} received in GameController._gameModelEventHandler`);
            }
        }

        // Notify game mode of event.
        self.gameMode.gameEventHandler(self.gameMode, id, evt, data);
    }

    _roundModelEventHandler(self, id, evt, data) {
        // Notify game mode of event.
        self.gameMode.roundEventHandler(self.gameMode, id, evt, data);

        // Handle event.
        switch (evt) {
            case RoundModel._EVENTS.ROUND_ENDED:
            {
                // Emit updated game state.
                self.model.emitGameState(self.getGameState());

                self.log.debug("Round ended");
                self.model.setState(GameModel._STATES.BETWEEN_ROUNDS);
                break;
            }
            case RoundModel._EVENTS.TANK_DESTROYED:
            case RoundModel._EVENTS.TANK_KILLED:
            {
                // Emit updated game state.
                self.model.emitGameState(self.getGameState());
                break;
            }
            case RoundModel._EVENTS.MAZE_SET:
            case RoundModel._EVENTS.TANK_CREATED:
            case RoundModel._EVENTS.TANK_CHANGED:
            case RoundModel._EVENTS.TANK_CHICKENED_OUT:
            case RoundModel._EVENTS.PROJECTILE_CREATED:
            case RoundModel._EVENTS.PROJECTILE_DESTROYED:
            case RoundModel._EVENTS.PROJECTILE_TIMEOUT:
            case RoundModel._EVENTS.TRAP_CREATED:
            case RoundModel._EVENTS.TRAP_TRIPPED:
            case RoundModel._EVENTS.TRAP_DESTROYED:
            case RoundModel._EVENTS.COLLECTIBLE_CREATED:
            case RoundModel._EVENTS.COLLECTIBLE_DESTROYED:
            case RoundModel._EVENTS.WEAPON_CREATED:
            case RoundModel._EVENTS.WEAPON_DESTROYED:
            case RoundModel._EVENTS.UPGRADE_CREATED:
            case RoundModel._EVENTS.UPGRADE_DESTROYED:
            case RoundModel._EVENTS.COUNTER_CREATED:
            case RoundModel._EVENTS.COUNTER_DESTROYED:
            case RoundModel._EVENTS.ZONE_CREATED:
            case RoundModel._EVENTS.ZONE_DESTROYED:
            case RoundModel._EVENTS.PROJECTILE_MAZE_COLLISION:
            case RoundModel._EVENTS.PROJECTILE_SHIELD_COLLISION:
            case RoundModel._EVENTS.PROJECTILE_ZONE_COLLISION:
            case RoundModel._EVENTS.TANK_TANK_COLLISION:
            case RoundModel._EVENTS.TANK_MAZE_COLLISION:
            case RoundModel._EVENTS.TANK_COLLECTIBLE_COLLISION:
            case RoundModel._EVENTS.TANK_DEADLY_COLLISION:
            case RoundModel._EVENTS.TANK_TRAP_COLLISION:
            case RoundModel._EVENTS.TANK_TRAP_END_COLLISION:
            case RoundModel._EVENTS.TANK_SHIELD_COLLISION:
            case RoundModel._EVENTS.TANK_ZONE_COLLISION:
            case RoundModel._EVENTS.SHIELD_SHIELD_COLLISION:
            case RoundModel._EVENTS.SHIELD_ZONE_COLLISION:
            case RoundModel._EVENTS.TRAP_ZONE_COLLISION:
            case RoundModel._EVENTS.ROUND_CREATED:
            case RoundModel._EVENTS.ROUND_STARTED:
            case RoundModel._EVENTS.CELEBRATION_STARTED:
            case RoundModel._EVENTS.CELEBRATION_ENDED: // FIXME Possibly emit game state?
            case Weapon._EVENTS.WEAPON_FIRED:
            case Weapon._EVENTS.WEAPON_DELAYED_FIRE:
            case Weapon._EVENTS.WEAPON_EMPTY:
            case Weapon._EVENTS.HOMING_MISSILE_TARGET_CHANGED:
            case Weapon._EVENTS.MINE_ACTIVATED:
            case Weapon._EVENTS.MINE_TRIPPED:
            case Weapon._EVENTS.MINE_DETONATED:
            case Upgrade._EVENTS.UPGRADE_ACTIVATED:
            case Upgrade._EVENTS.UPGRADE_WEAKENED:
            case Upgrade._EVENTS.UPGRADE_STRENGTHENED:
            case Zone._EVENTS.ZONE_ENTERED:
            case Zone._EVENTS.ZONE_LEFT:
            case Zone._EVENTS.ZONE_DESTABILISED:
            {
                // Intentionally do nothing here
                break;
            }
            default:
            {
                self.log.error(`Unknown event ${evt} received in GameController._roundModelEventHandler`);
            }
        }
    }

    getB2DWorld() {
        if (this.roundController) {
            return this.roundController.getB2DWorld();
        }
    }
}
